// TMOS Action Service (RAIL System)
// Date: December 9, 2025
// Purpose: Frontend service for Action Management System API
//
// This service provides all methods for interacting with the Action Management System:
// - CRUD operations for actions
// - Assignment management
// - Context association
// - Status management
// - Comments and history

import api from './api';

// Action status enum - using existing database ENUM values
export type ActionStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'ON_HOLD';

// Action priority enum - using existing database ENUM values
export type ActionPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

// Action interface matching backend
export interface Action {
  id: string;
  tenant_id: string;
  title: string;
  description?: string | null;
  status: ActionStatus;
  priority: ActionPriority;
  activity_category_id?: string | null;
  activity_category_name?: string | null;
  due_date?: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  completed_at?: string | null;
  completed_by?: string | null;
  completed_by_name?: string | null;
  business_hierarchy_id?: string | null;
  functional_hierarchy_id?: string | null;
  geographic_hierarchy_id?: string | null;
  returned_at?: string | null;
  returned_by?: string | null;
  returned_by_name?: string | null;
  return_reason?: string | null;
  deleted_at?: string | null;
  creator_name?: string;
  assigned_users?: ActionAssignment[];
  contexts?: ActionContext[];
}

// Action assignment interface
export interface ActionAssignment {
  id: string;
  action_id: string;
  user_id: string;
  user_name?: string;
  user_display?: string; // "First Last (Title)" for assignee
  assigned_by: string;
  assigned_by_name?: string;
  assigned_by_display?: string; // "First Last (Title)" for assigner
  assigned_at: string;
  completed_at?: string | null;
}

// Action context interface (polymorphic association)
export interface ActionContext {
  id: string;
  action_id: string;
  context_type: string; // 'risk', 'issue', 'project', 'transformation', etc.
  context_id: string;
  created_at: string;
}

// Action comment interface
export interface ActionComment {
  id: string;
  action_id: string;
  user_id: string;
  user_name?: string;
  comment_text: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

// Action history interface
export interface ActionHistory {
  id: string;
  action_id: string;
  user_id: string;
  user_name?: string;
  user_email?: string;
  change_type: string;
  old_value?: string | null;
  new_value?: string | null;
  comment?: string | null;
  created_at: string;
}

// Request interfaces
export interface CreateActionRequest {
  title: string;
  description?: string;
  priority?: ActionPriority;
  activity_category_id?: string;
  due_date?: string;
  context_type?: string; // Auto-associate if creating from entity page
  context_id?: string; // Auto-associate if creating from entity page
  assigned_user_ids?: string[]; // Array of user IDs to assign
  business_hierarchy_id?: string | null;
  functional_hierarchy_id?: string | null;
  geographic_hierarchy_id?: string | null;
}

export interface UpdateActionRequest {
  title?: string;
  description?: string;
  priority?: ActionPriority;
  activity_category_id?: string | null;
  due_date?: string | null;
  business_hierarchy_id?: string | null;
  functional_hierarchy_id?: string | null;
  geographic_hierarchy_id?: string | null;
}

export interface ActionFilters {
  status?: ActionStatus;
  priority?: ActionPriority;
  assigned_to_user_id?: string;
  created_by_user_id?: string;
  activity_category_id?: string;
  context_type?: string;
  context_id?: string;
  due_date_from?: string;
  due_date_to?: string;
  search?: string;
}

/**
 * ActionService class
 * 
 * Provides all methods for interacting with the Action Management System API.
 * All methods handle errors and return appropriate data structures.
 */
class ActionService {
  /**
   * Get actions for current user (with hierarchy filtering)
   * 
   * @param filters - Optional filters for actions
   * @returns Array of actions
   */
  async getActions(filters: ActionFilters = {}): Promise<Action[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const queryString = params.toString();
    const response = await api.get(`/actions${queryString ? `?${queryString}` : ''}`);
    return response.data.data || [];
  }

  /**
   * Get actions assigned to me
   * 
   * @param filters - Optional filters
   * @returns Array of actions assigned to current user
   */
  async getMyActions(filters: ActionFilters = {}): Promise<Action[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const queryString = params.toString();
    const response = await api.get(`/actions/my-actions${queryString ? `?${queryString}` : ''}`);
    return response.data.data || [];
  }

  /**
   * Get actions I created
   * 
   * @param filters - Optional filters
   * @returns Array of actions created by current user
   */
  async getActionsICreated(filters: ActionFilters = {}): Promise<Action[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const queryString = params.toString();
    const response = await api.get(`/actions/i-created${queryString ? `?${queryString}` : ''}`);
    return response.data.data || [];
  }

  /**
   * Get actions I am responsible for (same as my-actions for now)
   * 
   * @param filters - Optional filters
   * @returns Array of actions user is responsible for
   */
  async getActionsIAmResponsibleFor(filters: ActionFilters = {}): Promise<Action[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const queryString = params.toString();
    const response = await api.get(`/actions/i-am-responsible${queryString ? `?${queryString}` : ''}`);
    return response.data.data || [];
  }

  /**
   * Get action by ID
   * 
   * @param id - Action ID
   * @returns Action with all related data
   */
  async getActionById(id: string): Promise<Action> {
    const response = await api.get(`/actions/${id}`);
    return response.data.data;
  }

  /**
   * Create new action
   * 
   * @param data - Action creation data
   * @returns Created action
   */
  async createAction(data: CreateActionRequest): Promise<Action> {
    const response = await api.post('/actions', data);
    return response.data.data;
  }

  /**
   * Update action (creator only)
   * 
   * @param id - Action ID
   * @param data - Update data
   * @returns Updated action
   */
  async updateAction(id: string, data: UpdateActionRequest): Promise<Action> {
    const response = await api.put(`/actions/${id}`, data);
    return response.data.data;
  }

  /**
   * Delete action (soft delete, creator only)
   * 
   * @param id - Action ID
   */
  async deleteAction(id: string): Promise<void> {
    await api.delete(`/actions/${id}`);
  }

  /**
   * Assign action to users
   * 
   * @param id - Action ID
   * @param userIds - Array of user IDs to assign
   * @returns Updated action
   */
  async assignAction(id: string, userIds: string[]): Promise<Action> {
    const response = await api.post(`/actions/${id}/assign`, { user_ids: userIds });
    return response.data.data;
  }

  /**
   * Unassign user from action
   * 
   * @param id - Action ID
   * @param userId - User ID to unassign
   * @returns Updated action
   */
  async unassignAction(id: string, userId: string): Promise<Action> {
    const response = await api.delete(`/actions/${id}/assign/${userId}`);
    return response.data.data;
  }

  /**
   * Associate action with context entity
   * 
   * @param id - Action ID
   * @param contextType - Type of entity ('risk', 'issue', 'project', etc.)
   * @param contextId - Entity ID
   * @returns Updated action
   */
  async associateActionWithContext(id: string, contextType: string, contextId: string): Promise<Action> {
    const response = await api.post(`/actions/${id}/contexts`, {
      context_type: contextType,
      context_id: contextId
    });
    return response.data.data;
  }

  /**
   * Remove action context association
   * 
   * @param id - Action ID
   * @param contextType - Type of entity
   * @param contextId - Entity ID
   * @returns Updated action
   */
  async removeActionContext(id: string, contextType: string, contextId: string): Promise<Action> {
    const response = await api.delete(`/actions/${id}/contexts/${contextType}/${contextId}`);
    return response.data.data;
  }

  /**
   * Get actions for context (Risk, Issue, Project, etc.)
   * 
   * @param contextType - Type of entity
   * @param contextId - Entity ID
   * @returns Array of actions for the context
   */
  async getActionsByContext(contextType: string, contextId: string): Promise<Action[]> {
    const response = await api.get(`/actions/context/${contextType}/${contextId}`);
    return response.data.data || [];
  }

  /**
   * Update action status (assigned users can modify status)
   * 
   * @param id - Action ID
   * @param status - New status
   * @param comment - Optional comment about the status change
   * @returns Updated action
   */
  async updateActionStatus(id: string, status: ActionStatus, comment?: string): Promise<Action> {
    const response = await api.put(`/actions/${id}/status`, { status, comment });
    return response.data.data;
  }

  /**
   * Complete action
   * 
   * @param id - Action ID
   * @param comment - Optional completion comment
   * @returns Completed action
   */
  async completeAction(id: string, comment?: string): Promise<Action> {
    const response = await api.post(`/actions/${id}/complete`, { comment });
    return response.data.data;
  }

  /**
   * Cancel action (creator only)
   * 
   * @param id - Action ID
   * @param reason - Reason for cancellation
   * @returns Cancelled action
   */
  async cancelAction(id: string, reason: string): Promise<Action> {
    const response = await api.post(`/actions/${id}/cancel`, { reason });
    return response.data.data;
  }

  /**
   * Return action to assigner
   * 
   * @param id - Action ID
   * @param reason - Return reason (required)
   * @returns Returned action
   */
  async returnAction(id: string, reason: string): Promise<Action> {
    const response = await api.post(`/actions/${id}/return`, { reason });
    return response.data.data;
  }

  /**
   * Get action comments
   * 
   * @param id - Action ID
   * @returns Array of comments
   */
  async getActionComments(id: string): Promise<ActionComment[]> {
    const response = await api.get(`/actions/${id}/comments`);
    return response.data.data || [];
  }

  /**
   * Add comment to action
   * 
   * @param id - Action ID
   * @param commentText - Comment text
   * @returns Created comment
   */
  async addComment(id: string, commentText: string): Promise<ActionComment> {
    const response = await api.post(`/actions/${id}/comments`, { comment_text: commentText });
    return response.data.data;
  }

  /**
   * Update comment (own comments only)
   * 
   * @param id - Action ID
   * @param commentId - Comment ID
   * @param commentText - Updated comment text
   * @returns Updated comment
   */
  async updateComment(id: string, commentId: string, commentText: string): Promise<ActionComment> {
    const response = await api.put(`/actions/${id}/comments/${commentId}`, { comment_text: commentText });
    return response.data.data;
  }

  /**
   * Delete comment (own comments only)
   * 
   * @param id - Action ID
   * @param commentId - Comment ID
   */
  async deleteComment(id: string, commentId: string): Promise<void> {
    await api.delete(`/actions/${id}/comments/${commentId}`);
  }

  /**
   * Get action history (creator only)
   * 
   * @param id - Action ID
   * @returns Array of history entries
   */
  async getActionHistory(id: string): Promise<ActionHistory[]> {
    const response = await api.get(`/actions/${id}/history`);
    return response.data.data || [];
  }
}

// Export singleton instance
export const actionService = new ActionService();
export default actionService;

